import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { SpacerValueOrKey } from "@faire/web/slate/spacing/getSpacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { popSignUp, SignUpElement, SignUpType } from "@faire/retailer/lib/popSignUp";
import { useIncentiveForLandingPages } from "@faire/retailer/views/PartnershipIncentiveLanding/Util";

export const IncentiveGetStarted: React.FC<{
  backgroundColor: string;
  isAmex?: boolean;
  isLoggedOut?: boolean;
}> = ({ backgroundColor, isAmex, isLoggedOut }) => {
  const sourceId = isAmex
    ? "amex_get_started_last_action_Feb_2024_incentive"
    : "square_get_started_last_action_Feb_2024_incentive";

  const incentive = useIncentiveForLandingPages();

  //TODO: Add tracking event when open signup modal
  const handleSignUp = () =>
    popSignUp(SignUpType.GENERIC, () => null, {
      element: SignUpElement.BELOW_FOLD,
      sourceId: sourceId,
    });

  return (
    <Wrapper $backgroundColor={backgroundColor}>
      {isLoggedOut ? (
        <>
          <Typography
            color={Core.text.default}
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              tabletAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="Ready to get started?"
              description={{
                text: "Header for getting started section",
                img: "https://cdn.faire.com/fastly/1d0b548dde7bed480e1c7d625dde9c5ca0b372ae4a32f7ccfa1f627b592840dd.png",
              }}
            />
          </Typography>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Sign up on Faire and get 50% off your first order"
              description={{
                text: "Subtitle for getting started section",
                img: "https://cdn.faire.com/fastly/1d0b548dde7bed480e1c7d625dde9c5ca0b372ae4a32f7ccfa1f627b592840dd.png",
              }}
            />
          </Typography>
          <ActionButton onClick={handleSignUp}>
            <StrictLocalMsg
              defaultMessage="Sign Up Now"
              description={{
                text: "Sign up button text",
                img: "https://cdn.faire.com/fastly/1d0b548dde7bed480e1c7d625dde9c5ca0b372ae4a32f7ccfa1f627b592840dd.png",
              }}
            />
          </ActionButton>
        </>
      ) : null}
      {isAmex ? (
        <TermsText $marginTop="2x">
          <StrictLocalMsg
            defaultMessage="*Offer Terms: This offer is made by Faire Wholesale, Inc. (“Faire”) and not by American Express. To redeem the 50% (up to {formattedIncentiveMoney}) discount offer you must first create a new Faire account by December 31, 2024 through this offer and make a purchase within seven (7) days of creating the new account. The 50% discount will automatically apply at checkout and will not apply to shipping and taxes. Your Faire account and the information you provide to Faire will be subject to Faire's Terms of Service and Privacy Policy. This offer cannot be combined with any other offer and is not transferrable. Limit 1 offer redemption per American Express merchant. You will not earn 50% after your first order on your new Faire account, even if you do not earn the entire {formattedIncentiveMoney} discount on your first order. Fulfillment of the offer is the sole responsibility of Faire. Offer is subject to change at any time without notice to you."
            description={{
              text: "Text for the offer description",
              img: "https://cdn.faire.com/fastly/2fd0b5785b20e1fb79a96238eed43c446c949b4f325182d171b61dd2de8a284d.png",
            }}
            values={{
              formattedIncentiveMoney: incentive,
            }}
          />
        </TermsText>
      ) : (
        <Column>
          <TermsText $marginTop="2x">
            <StrictLocalMsg
              defaultMessage="Maximum discount is {formattedIncentiveMoney}. Offer ends 7 days after signing up."
              description={{
                text: "Text for the offer description",
                img: "https://cdn.faire.com/fastly/f9ea31e17cb2bdd801ba3df2f10961d3e2eed14534049a1999acec1f4ffdcf88.png",
              }}
              values={{ formattedIncentiveMoney: incentive }}
            />
          </TermsText>
          <TermsText $marginTop="0">
            <StrictLocalMsg
              defaultMessage="*To be eligible for the discount offer you must be a Square merchant and a new Faire customer. Offer is subject to change at any time without notice to you."
              description={{
                text: "Text for the offer terms",
                img: "https://cdn.faire.com/fastly/f9ea31e17cb2bdd801ba3df2f10961d3e2eed14534049a1999acec1f4ffdcf88.png",
              }}
            />
          </TermsText>
        </Column>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Column).attrs({
  align: "center",
  gap: "3x",
})<{ $backgroundColor: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  text-align: center;
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("6x", "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 96px 260px;
  }
`;

const ActionButton = styled(Button)`
  margin-top: ${getSpacing("1x")};
  ${responsiveMediaQueries.desktopAndAbove} {
    margin-top: ${getSpacing("3x")};
  }
`;

const TermsText = styled(Typography).attrs({
  color: Core.text.subdued,
  variant: "labelSansRegular",
})<{ $marginTop: SpacerValueOrKey }>`
  margin-top: ${({ $marginTop }) => getSpacing($marginTop)};
  ${responsiveMediaQueries.tablet} {
    width: 700px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 920px;
  }
`;
